// copy this print page later for all print pages
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { ArrowLeft, Printer } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import QRCode from 'react-qr-code';
import { useLocation, useNavigate } from 'react-router-dom';
const BC_ORPrintPage = () => {
   const location = useLocation();
   const navigate = useNavigate();
   const printRef = useRef();
   const oop = location.state?.oop;

   useEffect(() => {
      if (!oop) {
         navigate('/personnel/bill-collector');
         return;
      }
   }, [oop, navigate]);

   const handlePrint = () => {
      window.print();
   };

   const handleBack = () => {
      navigate('/personnel/bill-collector');
   };

   if (!oop || !oop.officialReceipt) {
      return (
         <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">Error</h1>
            <p>No official receipt data found.</p>
            <Button onClick={handleBack} className="mt-4">
               <ArrowLeft className="mr-2 h-4 w-4" />
               Back to Dashboard
            </Button>
         </div>
      );
   }

   const { officialReceipt } = oop;

   // Generate QR code value with essential OR details
   const qrValue = JSON.stringify({
      orNumber: officialReceipt.orNumber,
      billNo: oop.billNo,
      applicationId: oop.applicationId,
      amount: officialReceipt.amount,
      dateIssued: officialReceipt.dateIssued
   });

   const formatDate = (timestamp) => {
      const date = new Date(parseInt(timestamp));
      return format(date, 'M/d/yyyy');
   };

   return (
      <>
         <Helmet>
            <style type="text/css">{`
               @media print {
                  @page {
                     size: portrait;
                     margin: 0.5cm;
                  }
                  body {
                     -webkit-print-color-adjust: exact !important;
                     print-color-adjust: exact !important;
                     background-color: white !important;
                  }
                  .print-container {
                     padding: 0 !important;
                     max-width: 100% !important;
                  }
                  .print-hidden {
                     display: none !important;
                  }
                  /* Hide all navigation elements */
                  nav, header, footer, aside, .sidebar, .navbar {
                     display: none !important;
                  }
                  /* Ensure full page width */
                  html, body {
                     width: 100% !important;
                     margin: 0 !important;
                     padding: 0 !important;
                     overflow: visible !important;
                  }
                  /* Reset any fixed positioning */
                  #root, [data-overlay-container="true"] {
                     position: static !important;
                     overflow: visible !important;
                     height: auto !important;
                  }
               }
            `}</style>
            <title>Official Receipt - {officialReceipt.orNumber}</title>
         </Helmet>

         <div className="container mx-auto p-4 print-container">
            <div className="flex justify-between items-center mb-6 print-hidden">
               <Button variant="outline" onClick={handleBack}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Dashboard
               </Button>
               <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
                  <Printer className="mr-2 h-4 w-4" />
                  Print Receipt
               </Button>
            </div>

            {/* Print Template */}
            <div ref={printRef} className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm mx-auto max-w-3xl print:border-0 print:shadow-none print:p-0">
               <div className="text-center mb-6">
                  <h2 className="text-xl font-bold">OFFICIAL RECEIPT</h2>
                  <p className="text-sm text-gray-600">Department of Environment and Natural Resources</p>
                  <p className="text-sm text-gray-600">PENRO Marinduque</p>
               </div>

               <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                     <p><span className="font-semibold">OR Number:</span> {officialReceipt.orNumber}</p>
                     <p><span className="font-semibold">Date Issued:</span> {formatDate(officialReceipt.dateIssued)}</p>
                     <p><span className="font-semibold">Payment Method:</span> {officialReceipt.paymentMethod}</p>
                  </div>
                  <div>
                     <p><span className="font-semibold">Application No:</span> {oop.applicationId}</p>
                     <p><span className="font-semibold">Bill No:</span> {oop.billNo}</p>
                     <p><span className="font-semibold">Amount:</span> ₱{officialReceipt.amount.toFixed(2)}</p>
                  </div>
               </div>

               <div className="mb-6">
                  <p><span className="font-semibold">Payee:</span> {oop.namePayee}</p>
                  <p><span className="font-semibold">Address:</span> {oop.address}</p>
                  <p><span className="font-semibold">Nature of Application:</span> {oop.natureOfApplication}</p>
               </div>

               {officialReceipt.remarks && (
                  <div className="mb-6">
                     <p><span className="font-semibold">Remarks:</span></p>
                     <p className="text-gray-700">{officialReceipt.remarks}</p>
                  </div>
               )}

               <div className="flex justify-center mb-6">
                  <div className="p-4 bg-white">
                     <QRCode
                        value={qrValue}
                        size={128}
                        style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                        viewBox={`0 0 128 128`}
                     />
                  </div>
               </div>

               <div className="text-center text-sm text-gray-500 print:mt-8">
                  <p>This is a system-generated official receipt.</p>
                  <p>Verify authenticity by scanning the QR code.</p>
               </div>
            </div>
         </div>
      </>
   );
};

export default BC_ORPrintPage;